import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { globalSearchResponseSchema, type GlobalSearchResponse } from '@tailoring-crm/shared';
import { useApiClient } from '../api/api-context.js';

/**
 * The global search box's one call.
 *
 * `enabled` on a two-character floor, matching the server's own refusal: a box that fires on
 * the first keystroke spends a request per letter to search for "P", and the answer to "P" is
 * the whole shop.
 *
 * Results are cached for a few seconds and no longer. Somebody who searches, opens a customer
 * and comes back expects the same list; somebody who searches an hour later, after booking
 * three orders, does not.
 */
export function useGlobalSearch(
  query: string,
  options: { enabled?: boolean } = {},
): UseQueryResult<GlobalSearchResponse> {
  const client = useApiClient();
  const term = query.trim();
  return useQuery({
    queryKey: ['search', term],
    queryFn: () =>
      client.get('/search', { schema: globalSearchResponseSchema, query: { q: term } }),
    enabled: (options.enabled ?? true) && term.length >= 2,
    staleTime: 10_000,
    // A search box must never blank under somebody's hand while they read the results.
    placeholderData: (previous) => previous,
  });
}
